import { useFeatureGate } from '@statsig/react-bindings';
import { Navigate, useParams } from 'react-router-dom';

import { telehealthVisitorRouteRedirectGate } from '../../statsig/gates';

/**
 * Redirects visitor URLs where the patient short ID has trailing punctuation
 * (e.g. periods, commas, exclamation marks, quotes) to the same URL with the
 * punctuation stripped. This handles a rare but possible (observationally <1%
 * in production) case where clients copy their session link from an email or
 * text message and accidentally include trailing punctuation from the
 * surrounding sentence.
 *
 * It is presently the case and an assumption for the future that patient short
 * IDs only contain, letters, numbers, hyphens, and underscores. Therefore, any
 * trailing characters that are not in this set are safe to strip.
 *
 * Trailing punctuation is only stripped from the patient short ID, so any hash
 * or search params are preserved.
 *
 * This component should render before WithVisitorRoutePreRender, so that
 * data-fetching hooks are not called with an incorrect patient short ID.
 */
export function VisitorRouteRedirect({ children }: React.PropsWithChildren) {
  const { patientShortId, providerShortId } = useParams<{
    patientShortId: string;
    providerShortId: string;
  }>();
  const { value: isRedirectEnabled } = useFeatureGate(telehealthVisitorRouteRedirectGate);

  if (!isRedirectEnabled) return children;

  const cleanedPatientShortId = patientShortId?.replace(/[^a-zA-Z0-9-_]+$/, '');
  if (patientShortId === cleanedPatientShortId) return children;

  return <Navigate to={`/${providerShortId}/${cleanedPatientShortId}`} replace />;
}
